'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Trash2 } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { PageContainer } from '@/components/layout/PageContainer';
import type { Cart, Product } from '@/types';

export function CartPage() {
  const { user } = useAuthStore();
  const { addToast } = useToast();
  const [cart, setCart] = useState<Cart | null>(null);
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadCart() {
    if (!user) return;
    setLoading(true);
    try {
      const cartData = await api.cart.getCart();
      setCart(cartData);
      
      if (cartData) {
        const productData: Record<string, Product> = {};
        for (const item of cartData.items) {
          const product = await api.products.getProduct(item.productId);
          if (product) productData[item.productId] = product;
        }
        setProducts(productData);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleUpdateQty = async (productId: string, qty: number) => {
    if (!user) return;
    try {
      await api.cart.updateItemQty(productId, qty);
      await loadCart();
      addToast({ title: 'Сагс шинэчлэгдсэн', description: 'Тоо ширхэг амжилттай шинэчлэгдлээ' });
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Сагс шинэчлэхэд алдаа гарлаа', variant: 'destructive' });
    }
  };

  const handleRemove = async (productId: string) => {
    if (!user) return;
    try {
      await api.cart.removeItem(productId);
      await loadCart();
      addToast({ title: 'Устгасан', description: 'Бараа сагснаас устгагдлаа' });
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Бараа устгахэд алдаа гарлаа', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </PageContainer>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <PageContainer>
        <h1 className="text-3xl font-bold mb-6">Сагс</h1>
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Таны сагс хоосон байна</p>
          <Link href="/">
            <Button>Худалдаа үргэлжлүүлэх</Button>
          </Link>
        </div>
      </PageContainer>
    );
  }

  const total = cart.items.reduce((sum, item) => {
    const product = products[item.productId];
    if (!product) return sum;
    const price = product.salePrice || product.price;
    return sum + price * item.qty;
  }, 0);

  return (
    <PageContainer>
      <h1 className="text-3xl font-bold mb-6">Сагс</h1>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Бараа</TableHead>
              <TableHead>Үнэ</TableHead>
              <TableHead>Тоо ширхэг</TableHead>
              <TableHead>Нийт</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cart.items.map((item) => {
              const product = products[item.productId];
              if (!product) return null;
              const price = product.salePrice || product.price;
              return (
                <TableRow key={item.productId} data-testid={`cart-item-${item.productId}`}>
                  <TableCell>
                    <div className="flex items-center gap-4">
                      <img
                        src={product.images[0]?.url || '/placeholder.png'}
                        alt={product.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div>
                        <Link href={`/product/${product.id}`} className="font-semibold hover:underline">
                          {product.title}
                        </Link>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{formatCurrency(price)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQty(item.productId, item.qty - 1)}
                      >
                        -
                      </Button>
                      <span className="w-12 text-center">{item.qty}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQty(item.productId, item.qty + 1)}
                      >
                        +
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(price * item.qty)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.productId)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="mt-8 flex justify-end">
        <div className="w-full max-w-md space-y-4">
          <div className="flex justify-between text-lg">
            <span>Дэд нийт:</span>
            <span className="font-semibold">{formatCurrency(total)}</span>
          </div>
          <Link href="/checkout" className="block">
            <Button data-testid="checkout-start" className="w-full" size="lg">
              Төлбөр төлөх
            </Button>
          </Link>
        </div>
      </div>
    </PageContainer>
  );
}

